import {
  BarInterval,
  MonitorStatus,
  StrategyCategory,
  RegenerateStatus,
  OrderSide,
  RankingAlgorithm,
  ComparisonOperator,
} from '@/services/enums';
import { apiClient } from './apiClient';
import {
  MonitorRulesResultDto,
  MonitorSignalCondition as BaseMonitorSignalCondition,
  MonitorSignalRule,
  PositionSuggestion,
  KlineCandle,
  MonitorIndicators,
} from './klineTypes';

export interface MonitorSignalCondition extends BaseMonitorSignalCondition {
  met?: boolean; // 条件是否已满足（由后端检查后设置）
  currentValue?: number | boolean; // 当前值（由后端检查后设置，对于 cross_up/cross_down 为布尔值）
  ema20?: number; // EMA20值（仅当field为price时设置）
}

export interface MonitorSignal extends Omit<MonitorSignalRule, 'conditions'> {
  type: OrderSide;
  conditions: Array<MonitorSignalCondition>;
  triggered: boolean;
  triggeredAt?: string;
}
export interface MonitorTask {
  monitorId: string;
  symbol: string;
  bar: BarInterval;
  status: MonitorStatus;
  enableAutoTrading?: boolean;
  createdAt: string;
  updatedAt?: string; // 任务更新时间
  ruleStartTime?: number;
  ruleEndTime?: number;
  analysisScore?: number; // AI分析报告评分 (0-100)
  analysisDescription?: string; // AI分析报告简述
  promptHash?: string; // prompt 的 hash
  analysisReportHash?: string; // 分析报告的 hash
  systemPromptId?: string; // 系统提示词ID（字符串格式，对应 promptId 字段）
  regenerateStatus?: RegenerateStatus; // 重新生成监控规则任务状态
  regenerateFailureReason?: string; // 规则重新生成失败原因
  signals: Array<MonitorSignal>;
  fixedRules?: Array<MonitorSignalRule>; // 固定监控规则
  positionSuggestion?: PositionSuggestion | null;
  currentData?: MonitorIndicators;
}

export interface StartMonitorRequest {
  symbol: string;
  bar: BarInterval;
  aiAnalysis: string;
  enableAutoTrading?: boolean;
  monitorRules?: MonitorRulesResultDto;
  ruleStartTime?: number;
  ruleEndTime?: number;
}

export interface StartMonitorResponse {
  monitorId: string;
  signals: Array<MonitorSignal>;
}

export class KlineMonitorService {
  /**
   * 获取所有监控任务（支持可选的 status 和 symbol 过滤）
   */
  async getAllRunningMonitors(status?: MonitorStatus, symbol?: string): Promise<MonitorTask[]> {
    const params: any = {};
    if (status) {
      params.status = status;
    }
    if (symbol) {
      params.symbol = symbol;
    }

    const response = await apiClient.get<MonitorTask[]>('/kline/monitor/list', { params });

    if (!response.success) {
      throw new Error(response.message || '获取监控任务列表失败');
    }

    return response.data || [];
  }

  /**
   * 获取所有监控任务 (包装 getAllRunningMonitors)
   */
  async getAllMonitors(symbol?: string): Promise<MonitorTask[]> {
    return this.getAllRunningMonitors(undefined, symbol);
  }

  /**
   * 获取K线数据
   */
  async getKlines(
    symbol: string,
    bar: string,
    limit?: number,
    options?: { startTime?: number; endTime?: number },
  ): Promise<KlineCandle[]> {
    const params: any = { symbol, bar, limit };
    if (options?.startTime) params.startTime = options.startTime;
    if (options?.endTime) params.endTime = options.endTime;

    const response = await apiClient.get<KlineCandle[]>('/kline/data', { params });

    if (!response.success) {
      throw new Error(response.message || '获取K线数据失败');
    }
    return response.data;
  }

  /**
   * 启动监控
   */
  async startMonitor(
    symbol: string,
    bar: BarInterval,
    enableAutoTrading: boolean = false,
    fixedRuleSignals: Array<{
      type: OrderSide;
      description: string;
      conditions: Array<{
        field: string;
        operator: ComparisonOperator;
        value: number;
      }>;
      triggered: boolean;
    }> = [],
  ): Promise<{
    monitorId: string;
    signals: Array<{
      type: OrderSide;
      description: string;
      conditions: Array<{
        field: string;
        operator: ComparisonOperator;
        value: number;
      }>;
      triggered: boolean;
      triggeredAt?: string;
    }>;
  }> {
    try {
      const response = await apiClient.post<{
        monitorId: string;
        signals: Array<any>;
      }>('/kline/monitor/start', {
        symbol,
        bar,
        enableAutoTrading,
        fixedRuleSignals,
      });

      if (!response.success) {
        throw new Error(response.message || '启动监控失败');
      }

      return response.data;
    } catch (error: any) {
      console.error('启动监控失败:', error);
      throw error;
    }
  }

  /**
   * 重新启动监控任务（只需要 symbol 和 bar，自动判断是否使用缓存）
   */
  async restartMonitor(symbol: string, bar: string, enableAutoTrading: boolean = false): Promise<StartMonitorResponse> {
    const response = await apiClient.post<StartMonitorResponse>('/kline/monitor/start', {
      symbol,
      bar,
      enableAutoTrading,
    });

    if (!response.success) {
      throw new Error(response.message || '启动监控任务失败');
    }

    return response.data;
  }

  /**
   * 停止监控任务
   */
  async stopMonitor(monitorId: string): Promise<any> {
    const response = await apiClient.post('/kline/monitor/stop', { monitorId });

    if (!response.success) {
      throw new Error(response.message || '停止监控任务失败');
    }

    return response.data;
  }

  /**
   * 获取监控任务状态
   */
  async getMonitorStatus(monitorId: string): Promise<MonitorTask> {
    const response = await apiClient.get<MonitorTask>('/kline/monitor/status', {
      params: { monitorId },
    });

    if (!response.success) {
      throw new Error(response.message || '获取监控任务状态失败');
    }

    return response.data;
  }

  /**
   * 删除监控任务
   */
  async deleteMonitor(monitorId: string): Promise<any> {
    const response = await apiClient.delete('/kline/monitor', {
      params: { monitorId },
    });

    if (!response.success) {
      throw new Error(response.message || '删除监控任务失败');
    }

    return response.data;
  }

  /**
   * 批量删除停止运行的监控任务
   */
  async deleteStoppedMonitors(): Promise<number> {
    const response = await apiClient.delete<{ deletedCount: number }>('/kline/monitor/stopped');

    if (!response.success) {
      throw new Error(response.message || '批量删除监控任务失败');
    }

    return response.data?.deletedCount || 0;
  }

  /**
   * 发送交易信号到自动交易引擎
   * @param symbol 交易对
   * @param price 触发价格
   * @param monitorSignal 监控信号对象，如果为空则表示平仓
   */
  async sendAutoTradingSignal(
    symbol: string,
    price: number,
    monitorSignal?: MonitorSignal | null,
  ): Promise<{ taskId: string; message: string }> {
    const response = await apiClient.post<{ taskId: string; message: string }>('/kline/auto-trading/signal', {
      symbol,
      price,
      monitorSignal: monitorSignal || null,
    });

    if (!response.success) {
      throw new Error(response.message || '发送交易信号失败');
    }

    return response.data;
  }

  /**
   * 获取信号触发记录列表
   */
  async getSignalTriggers(params: {
    page?: number;
    pageSize?: number;
    symbol?: string;
    bar?: string;
    signalType?: OrderSide;
  }): Promise<{
    records: Array<{
      id: number;
      monitorId: number;
      symbol: string;
      bar: string;
      signalType: OrderSide;
      signalDescription: string;
      conditions: Array<{
        field: string;
        operator: ComparisonOperator;
        value: number;
        met?: boolean;
      }>;
      price: number;
      indicators: any;
      triggeredAt: string;
    }>;
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await apiClient.get<any>('/kline/monitor/signal-triggers', {
      params,
    });

    if (!response.success) {
      throw new Error(response.message || '获取信号触发记录失败');
    }

    return response.data;
  }

  /**
   * 根据symbol和bar查找正在运行的监控任务
   */
  async findRunningMonitor(symbol: string, bar: string): Promise<MonitorTask | null> {
    const response = await apiClient.get<MonitorTask | null>('/kline/monitor/find', {
      params: { symbol, bar },
    });

    if (!response.success) {
      throw new Error(response.message || '查找监控任务失败');
    }

    return response.data;
  }

  /**
   * 启动自主监控管理服务
   */
  async startAutoMonitor(
    enableAutoTrading: boolean = false,
    monitorCount: number = 1,
    excludedSymbols: string[] = [],
    systemPromptId?: string,
    tradingParams?: {
      leverage: number;
      positionSize: number;
      takeProfitPercent: number;
      stopLossPercent: number;
    },
    minVolume: number = 10000000,
    rankingAlgorithm: RankingAlgorithm = 'volatility',
    timeFrame: BarInterval = '1H',
  ): Promise<any> {
    const response = await apiClient.post('/kline/auto-monitor/start', {
      enableAutoTrading,
      monitorCount,
      excludedSymbols,
      systemPromptId,
      tradingParams,
      minVolume,
      rankingAlgorithm,
      timeFrame,
    });

    if (!response.success) {
      throw new Error(response.message || '启动自主监控失败');
    }

    return response.data;
  }

  /**
   * 停止自主监控管理服务
   */
  async stopAutoMonitor(): Promise<any> {
    const response = await apiClient.post('/kline/auto-monitor/stop', {});

    if (!response.success) {
      throw new Error(response.message || '停止自主监控失败');
    }

    return response.data;
  }

  /**
   * 获取自主监控管理服务状态
   */
  async getAutoMonitorStatus(): Promise<{ isRunning: boolean }> {
    const response = await apiClient.get<{ isRunning: boolean }>('/kline/auto-monitor/status');

    if (!response.success) {
      throw new Error(response.message || '获取自主监控状态失败');
    }

    return response.data;
  }

  /**
   * 获取自主监控管理服务配置
   */
  async getAutoMonitorConfig(): Promise<{
    isRunning: boolean;
    enableAutoTrading: boolean;
    monitorCount: number;
    minVolume: number;
    excludedSymbols: string[];
    rankingAlgorithm: StrategyCategory;
    timeFrame?: BarInterval;
    systemPromptId?: string;
    tradingParams?: {
      leverage: number;
      positionSize: number;
      takeProfitPercent: number;
      stopLossPercent: number;
    };
  } | null> {
    const response = await apiClient.get<{
      isRunning: boolean;
      enableAutoTrading: boolean;
      monitorCount: number;
      minVolume: number;
      excludedSymbols: string[];
      rankingAlgorithm: RankingAlgorithm;
      timeFrame?: BarInterval;
      systemPromptId?: string;
      tradingParams?: {
        leverage: number;
        positionSize: number;
        takeProfitPercent: number;
        stopLossPercent: number;
      };
    } | null>('/kline/auto-monitor/config');

    if (!response.success) {
      throw new Error(response.message || '获取自主监控配置失败');
    }

    return response.data;
  }

  /**
   * 预览自主监控将选中的币种
   */
  async previewAutoMonitorSymbols(
    count: number,
    excludedSymbols: string[] = [],
    minVolume: number = 10000000,
    rankingAlgorithm: RankingAlgorithm = 'volatility',
    timeFrame: BarInterval = '1H',
  ): Promise<Array<{ symbol: string; value: number }>> {
    const response = await apiClient.post<Array<{ symbol: string; value: number }>>(
      '/kline/auto-monitor/preview-symbols',
      {
        count,
        excludedSymbols,
        minVolume,
        rankingAlgorithm,
        timeFrame,
      },
    );

    if (!response.success) {
      throw new Error(response.message || '预览监控币种失败');
    }

    return response.data;
  }

  /**
   * 获取监控规则生成日志
   */
  async getMonitorRuleLogs(page: number = 1, pageSize: number = 20): Promise<{ logs: any[]; total: number }> {
    const response = await apiClient.get<{ logs: any[]; total: number }>('/kline/monitor/logs', {
      params: { page, pageSize },
    });

    if (!response.success) {
      throw new Error(response.message || '获取监控规则生成日志失败');
    }

    return response.data;
  }
}

export const klineMonitorService = new KlineMonitorService();
